import { z } from 'zod';

// Common validation schemas
export const CommonSchemas = {
  // Basic types
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(/^\+?[\d\s\-\(\)]+$/, 'Invalid phone number format'),
  url: z.string().url('Invalid URL format'),
  uuid: z.string().uuid('Invalid UUID format'),
  
  // Business-specific
  businessId: z.string().min(1, 'Business ID is required'),
  employeeId: z.string().min(1, 'Employee ID is required'),
  userId: z.string().min(1, 'User ID is required'),
  
  // Numeric validations
  positiveNumber: z.number().positive('Must be a positive number'),
  nonNegativeNumber: z.number().min(0, 'Must be non-negative'),
  percentage: z.number().min(0).max(100, 'Must be between 0 and 100'),
  rating: z.number().min(1).max(5, 'Rating must be between 1 and 5'),
  
  // Date validations
  pastDate: z.date().max(new Date(), 'Date must be in the past'),
  futureDate: z.date().min(new Date(), 'Date must be in the future'),
  dateString: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  
  // Text validations
  nonEmptyString: z.string().min(1, 'Field is required'),
  shortText: z.string().max(100, 'Text must be 100 characters or less'),
  mediumText: z.string().max(500, 'Text must be 500 characters or less'),
  longText: z.string().max(2000, 'Text must be 2000 characters or less'),
  
  // Common business fields
  businessName: z.string().min(1, 'Business name is required').max(100, 'Business name too long'),
  employeeName: z.string().min(1, 'Employee name is required').max(100, 'Employee name too long'),
  address: z.string().max(200, 'Address too long'),
  description: z.string().max(1000, 'Description too long'),
};

// Common object schemas
export const ObjectSchemas = {
  businessDetails: z.object({
    id: CommonSchemas.businessId,
    name: CommonSchemas.businessName,
    description: CommonSchemas.description.optional(),
    address: CommonSchemas.address.optional(),
    website: CommonSchemas.url.optional(),
    phone: CommonSchemas.phone.optional(),
    email: CommonSchemas.email.optional(),
  }),

  employeeBase: z.object({
    id: CommonSchemas.employeeId,
    name: CommonSchemas.employeeName,
    email: CommonSchemas.email,
    business_id: CommonSchemas.businessId,
  }),

  timeLogEntry: z.object({
    employee_id: CommonSchemas.employeeId,
    business_id: CommonSchemas.businessId,
    scanned_at: z.string().datetime('Invalid datetime format'),
    event_type: z.enum(['clock_in', 'clock_out', 'break_start', 'break_end']),
  }),

  reviewEntry: z.object({
    id: CommonSchemas.uuid.optional(),
    business_id: CommonSchemas.businessId,
    customer_name: CommonSchemas.nonEmptyString,
    email: CommonSchemas.email.optional(),
    rating: CommonSchemas.rating,
    content: CommonSchemas.longText.optional(),
    sentiment: z.enum(['positive', 'neutral', 'negative']).optional(),
  }),
};

// Validation utility functions
export const ValidationUtils = {
  // Validate and transform data
  validateAndTransform: <T>(schema: z.ZodSchema<T>, data: unknown): { success: true; data: T } | { success: false; errors: string[] } => {
    try {
      const validData = schema.parse(data);
      return { success: true, data: validData };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = error.errors.map(err => `${err.path.join('.')}: ${err.message}`);
        return { success: false, errors };
      }
      return { success: false, errors: ['Validation failed'] };
    }
  },

  // Safe parse with default values
  safeParseWithDefaults: <T>(schema: z.ZodSchema<T>, data: unknown, defaults: Partial<T> = {}): T => {
    const result = schema.safeParse(data);
    if (result.success) {
      return result.data;
    }
    return { ...defaults, ...data } as T;
  },

  // Validate business ID format
  isValidBusinessId: (id: string): boolean => {
    return CommonSchemas.businessId.safeParse(id).success;
  },

  // Validate email format
  isValidEmail: (email: string): boolean => {
    return CommonSchemas.email.safeParse(email).success;
  },

  // Validate phone format
  isValidPhone: (phone: string): boolean => {
    return CommonSchemas.phone.safeParse(phone).success;
  },

  // Validate URL format
  isValidUrl: (url: string): boolean => {
    return CommonSchemas.url.safeParse(url).success;
  },

  // Sanitize text input
  sanitizeText: (text: string): string => {
    return text.trim().replace(/\s+/g, ' ');
  },

  // Validate date range
  isValidDateRange: (startDate: Date, endDate: Date): boolean => {
    return startDate <= endDate;
  },

  // Format validation errors for display
  formatValidationErrors: (errors: z.ZodError): Record<string, string> => {
    const formatted: Record<string, string> = {};
    errors.errors.forEach(error => {
      const field = error.path.join('.');
      formatted[field] = error.message;
    });
    return formatted;
  },
};

// Helper function to create field validators
const createFieldValidator = <T>(schema: z.ZodSchema<T>) => (value: unknown) => {
  const result = schema.safeParse(value);
  return result.success ? null : result.error.errors[0].message;
};

// Form validation helpers
export const FormValidation = {
  // Create field-level validator
  createFieldValidator,

  // Create form-level validator
  createFormValidator: <T>(schema: z.ZodSchema<T>) => (data: unknown) => {
    const result = schema.safeParse(data);
    if (result.success) {
      return { isValid: true, data: result.data, errors: {} };
    }
    return {
      isValid: false,
      data: null,
      errors: ValidationUtils.formatValidationErrors(result.error)
    };
  },

  // Common field validators
  validators: {
    required: (value: any) => value ? null : 'This field is required',
    email: createFieldValidator(CommonSchemas.email),
    phone: createFieldValidator(CommonSchemas.phone),
    url: createFieldValidator(CommonSchemas.url),
    businessName: createFieldValidator(CommonSchemas.businessName),
    employeeName: createFieldValidator(CommonSchemas.employeeName),
    rating: createFieldValidator(CommonSchemas.rating),
  }
};

// Business rule validation schemas
export const BusinessRuleSchemas = {
  workHours: z.object({
    start: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format'),
    end: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format'),
  }),

  payRate: z.object({
    amount: CommonSchemas.positiveNumber,
    type: z.enum(['hourly', 'monthly', 'salary']),
    currency: z.string().length(3, 'Currency code must be 3 characters'),
  }),

  shiftConstraints: z.object({
    minHours: CommonSchemas.nonNegativeNumber,
    maxHours: CommonSchemas.positiveNumber,
    breakMinutes: CommonSchemas.nonNegativeNumber,
    overtimeThreshold: CommonSchemas.positiveNumber.optional(),
  }),
};

// Export all validation utilities for easy access